<template>
  <div class="table-layout" v-if="isTotal">
    <table class="table-container" border="1">
      <thead>
      <tr>
        <th class="table-header first" rowspan="5">전체요약</th>
      </tr>
      <tr>
        <th class="table-header">구분</th>
        <td class="table-date" v-for="label in headerLabels" :key="label">
          {{ label }}
        </td>
        <td class="table-date">계</td>
      </tr>
      <tr v-for="(item, index) in totalData" :key="'total_' + index">
        <th class="table-header">{{ item.sumNm }}</th>
        <td v-for="keys in sortedKeys" :key="keys">
          {{ inputComma(item[keys]) }}
        </td>
        <td class="table-total">{{ inputComma(item.total) }}</td>
      </tr>
      </thead>
    </table>
  </div>

  <div class="table-layout" v-else-if="isDefault">
    <table class="table-container" border="1">
      <thead>
      <tr>
        <th class="table-header" colspan="2">구분</th>
        <td class="table-date" v-for="label in headerLabels" :key="label">
          {{ label }}
        </td>
        <td class="table-date">계</td>
      </tr>
      </thead>
      <tbody>
      <template v-for="(item, index) in preparedDefaultData">
        <tr v-if="index === typeFirstIndex[item.typeNm]" :key="index + '_type'">
          <th class="table-header" :rowspan="typeCounts[item.typeNm] + 1">
            {{ item.typeNm }}
          </th>
        </tr>
        <tr :key="index + '_sum'">
          <td class="table-sub-header">{{ item.sumNm }}</td>
          <td
              v-for="keys in sortedKeys"
              :key="item.typeNm + '_' + item.sumNm + '_' + keys"
          >
            {{ inputComma(item[keys]) }}
          </td>
          <td class="table-total">{{ inputComma(item.total) }}</td>
        </tr>
      </template>
      </tbody>
    </table>
  </div>
</template>

<script>
import TableMixin from "@/mixin/TableMixin";

export default {
  name: "StaticsTable",
  props: [
    "isTotal",
    "isDefault",
    "isProduct",
    "isLoanType",
    "isLoanTypeGroup",
    "totalData",
    "defaultData",
    "paramsData"
  ],
  mixins: [TableMixin],
  computed: {
    headerLabels() {
      const { yyyy: year, type } = this.paramsData;
      const t = (type || "").toLowerCase();
      if (t === "y") return this.sortedYearKeys.map(k => k.slice(1));
      if (t === "q") return ["1분기", "2분기", "3분기", "4분기"];
      return Array.from({ length: 12 }, (_, i) => `${year}-${String(i+1).padStart(2, "0")}`);
    },
    sortedKeys() {
      const t = (this.paramsData.type || "").toLowerCase();
      return t === "y"
          ? this.sortedYearKeys
          : t === "q"
              ? this.sortedQuarterKeys
              : this.sortedMonthKeys;
    },
    sortedMonthKeys() {
      return this.getSortedKeysByPrefix("m", 12);
    },
    sortedQuarterKeys() {
      return this.getSortedKeysByPrefix("q", 4);
    },
    sortedYearKeys() {
      const data = this.isTotal ? this.totalData : this.defaultData;
      if (!data || data.length === 0) return [];
      const yearKeys = Array.from(
          new Set(data.flatMap(item => Object.keys(item).filter(k => k.startsWith("y"))))
      );
      return yearKeys.sort((a, b) => parseInt(a.slice(1), 10) - parseInt(b.slice(1), 10));
    },
    // 통계별 기본 타입 리스트 지정
    baseTypeList() {
      if (this.isLoanTypeGroup) return ["일반대출", "집단대출", "중도금이주비"]
      if (this.isProduct) return ["주택담보대출", "기금대출(보금자리)", "전세대출", "중도금이주비", "기타담보대출(부동산)", "기타담보대출(마이카)", "신용대출"];
      if (this.isLoanType) return ["집단대출", "그 외"];
      return [];
    },
    // 통계별 기본 하위 타입 리스트 지정
    baseSumList() {
      return (this.isProduct || this.isLoanType || this.isLoanTypeGroup)
          ? ["건수", "금액", "수수료"]
          : ["건수", "대출금액", "수수료"];
    },
    // 통계별 데이터 맵핑 정렬
    preparedDefaultData() {
      const templateArr = [];
      this.baseTypeList.forEach((type) => {
        this.baseSumList.forEach((sum) => {
          templateArr.push({
            typeNm: type,
            sumNm: sum,
            ...this.sortedKeys.reduce((acc, key) => {
              acc[key] = 0;
              return acc;
            }, {})
          });
        });
      });
      const dataMap = {};
      this.defaultData.forEach((item) => {
        const key = `${item.typeNm}||${item.sumNm}`;
        dataMap[key] = item;
      });
      return templateArr.map((t) => {
        const key = `${t.typeNm}||${t.sumNm}`;
        if (dataMap[key]) {
          return {
            typeNm: dataMap[key].typeNm,
            sumNm: dataMap[key].sumNm,
            ...this.sortedKeys.reduce((acc, k) => {
              acc[k] =
                  typeof dataMap[key][k] !== "undefined"
                      ? dataMap[key][k]
                      : 0;
              return acc;
            }, {}),
            total: dataMap[key].total || 0
          };
        }
        return {
          ...t,
          total: 0
        };
      });
    },
    // 과목 및 유형 타입 중복 값 제거
    typeFirstIndex() {
      const idxMap = {};
      this.preparedDefaultData.forEach((item, i) => {
        if (!(item.typeNm in idxMap)) {
          idxMap[item.typeNm] = i;
        }
      });
      return idxMap;
    },
    // 과목 및 유형 타입 중복 값 cnt 체크
    typeCounts() {
      const cnt = {};
      this.preparedDefaultData.forEach((item) => {
        cnt[item.typeNm] = cnt[item.typeNm] ? cnt[item.typeNm] + 1 : 1;
      });
      return cnt;
    }
  },
  methods: {
    getSortedKeysByPrefix(prefix, count) {
      if (!this.defaultData || this.defaultData.length === 0) {
        return Array.from({ length: count }, (_, i) => {
          const num = (i + 1).toString().padStart(prefix === 'm' ? 2 : 1, '0');
          return prefix + num;
        });
      }
      const firstItem = this.defaultData[0];
      return Object.keys(firstItem)
          .filter((k) => k.startsWith(prefix))
          .sort((a, b) => parseInt(a.slice(1), 10) - parseInt(b.slice(1), 10));
    }
  }
};
</script>

<style scoped lang="scss">
.table-layout {
  margin-bottom: 50px;
  text-wrap: nowrap;
  .table-container {
    border-collapse: collapse;
    border-left: 1px solid #222;
    border-bottom: 1px solid #222;
    .table-header.first {
      min-width: 100px;
    }
    .table-header {
      //min-width: 150px;
      min-width: 120px;
      font-size: 0.9rem;
      text-align: center;
      background-color: lightblue;
    }
    .table-date {
      font-weight: 500;
      text-align: center;
      background-color: skyblue;
    }
    .table-sub-header {
      //min-width: 100px;
      min-width: 100px;
      text-align: center;
      background-color: lightgray;
    }
    tr {
      td {
        text-align: right;
        padding: 2px 5px;
        font-size: 0.9rem;
        //font-size: 18px;
        //min-width: 180px;
        min-width: 140px;
        height: 30px;
      }
    }
    .table-total {
      background-color: #e0e0e0;
    }
  }
}
</style>
